import { ENABLE_EXTERNAL_LINK_FOR_IMAGE } from '@components/EmailSignaturev2/consts';
import EmailSignatureSpanButton from '@components/EmailSignaturev2/EmailSignatureSpanButton';
import { FormSelector, FormSelectorGroup } from '@components/FormSelector/FormSelector';
// components
import ImageEditor, {
  ImageEditorButton,
  ImageEditorButtons,
  ImageEditorFrame,
} from '@components/ImageEditor/ImageEditor';
import ImageSelector from '@components/ImageSelector/ImageSelector';
import Input from '@components/Input/Input';
import Popover, { positionBounded } from '@components/Popover/Popover';
import { SideEditorScrollType } from '@components/SideEditor/SideEditor';
// hooks and context
import { useSideEditorContext } from '@components/SideEditor/utils';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import {
  faCircle,
  faEllipsisV,
  faPencil,
  faSquareFull,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNode from '@hooks/use-node';
import { getURIFromURLFieldValue } from '@modules/card-helpers';
import {
  generateNewImageId,
  getImageTitle,
  getMakePrimaryOrSecondaryImageMessageForCompactLayout,
} from '@modules/email-signatures-helpers';
// utils
import Emitter from '@modules/event-emitter';
// biome-ignore lint/suspicious/noShadowRestrictedNames: historic
import { cloneDeep } from 'es-toolkit';
// es-toolkit/compat: lodash toString(null|undefined) === ''
import { toString } from 'es-toolkit/compat';
import {
  type FC,
  type MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Crop } from 'react-image-crop';

// consts and types
import {
  type EmailSignatureCrop,
  type EmailSignatureImage,
  EmailSignatureShapes,
  EmailSignatureSizes,
} from '../../types';
import EmailSignatureEditorImageViewMode from '../EmailSignatureEditorImageViewMode';
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import EmailSignatureSquircle from '../EmailSignatureSquircle';
import {
  EmailSignatureImageBox,
  EmailSignatureImagesBoxBody,
  EmailSignatureImagesBoxHeader,
  EmailSignatureImagesBoxHeaderContents,
  EmailSignatureImagesBoxHeaderDivider,
} from './EmailSignatureImageBox';
import styles from './EmailSignatureImageSection.module.scss';

type EmailSignatureEditorImageSectionProps = {
  show: boolean;
  image: EmailSignatureImage;
};

// We need the show prop to avoid a bug with react-image-crop + reach tabs. When you render the image
// in a tab that is hidden react-image-crop miscalculates the width of the image as 0 and doesn't display it.
const EmailSignatureEditorImageSection: FC<
  React.PropsWithChildren<EmailSignatureEditorImageSectionProps>
> = ({ show, image }) => {
  const {
    handleUpdateLocalCroppedImage,
    isInTransitionState,
    handleChangeImageViewMode,
    localCroppedImagesUrls,
    handleUploadOriginalImageUrl,
    displayImagesInViewMode,
    originalImagesUrls,
    handleUpdateImageShape,
    emailSignature,
    emailSignatureDispatch,
    isSignatureLocked,
  } = useContext(emailSignatureSideEditorServicesContext);
  const { scrollIntoView, changeTab, panelIndex, registerNode } = useSideEditorContext();

  const [imageSectionDiv, imageSectionDivRef] = useNode<HTMLDivElement>();
  const [showMakePrimaryPopover, setShowMakePrimaryPopover] = useState<boolean>(false);
  const [makePrimaryButtonEl, makePrimaryButtonElRef] = useNode<HTMLElement>();
  const [previouslyCroppedImageUrl, setPreviouslyCroppedImageUrl] = useState<string | null>(null);
  const [previousImageShape, setPreviousImageShape] = useState<EmailSignatureShapes | null>(null);
  const [previousCrop, setPreviousCrop] = useState<Crop>();
  const [openEditingBody, setOpenEditingBody] = useState<boolean>(true);

  const [externalLink, setExternalLink] = useState('');

  const neighboringImageIndex = useMemo(
    () => emailSignature.images.findIndex((imagesInList) => imagesInList.id !== image.id),
    [emailSignature.images, image.id],
  );

  const handleImageEditing = useCallback(
    (imageReference: string, crop: Crop) => {
      handleUpdateLocalCroppedImage(imageReference, image.id, image.shape, crop);
    },
    [handleUpdateLocalCroppedImage, image.id, image.shape],
  );

  const handleConfirmCroppedImage = () => {
    handleChangeImageViewMode(true, image.id);
  };

  const handleCancelCroppedImageChange = () => {
    if (!previouslyCroppedImageUrl) {
      return;
    }
    handleChangeImageViewMode(true, image.id);
    handleUpdateLocalCroppedImage(
      previouslyCroppedImageUrl,
      image.id,
      previousImageShape || EmailSignatureShapes.CIRCLE,
      previousCrop || null,
    );
    emailSignatureDispatch({
      type: 'UPDATE_IMAGE_SHAPE',
      value: { id: image.id, shape: previousImageShape || EmailSignatureShapes.CIRCLE },
    });

    emailSignatureDispatch({
      type: 'UPDATE_IMAGE_CROP',
      value: { id: image.id, crop: previousCrop || null },
    });
  };

  const handleDeleteImage = () => {
    emailSignatureDispatch({ type: 'DELETE_IMAGE', value: image.id });
  };

  const handleStartEditingImage = () => {
    handleChangeImageViewMode(false, image.id);

    setPreviouslyCroppedImageUrl(localCroppedImagesUrls[image.id] || '');
    setPreviousImageShape(image.shape);
    setPreviousCrop(image.crop || undefined);
  };

  const handleChangeOriginalImage = () => {
    setPreviouslyCroppedImageUrl('');
    setPreviousCrop(undefined);

    handleUploadOriginalImageUrl('', image.id);
    handleUpdateLocalCroppedImage('', image.id, image.shape, null);

    handleChangeImageViewMode(false, image.id);

    // This will create a new imageId for the image.
    emailSignatureDispatch({
      type: 'CLEAR_IMAGE_URL',
      value: { id: image.id },
    });
  };

  const handleChangeImageShape = (newValue: string) => {
    emailSignatureDispatch({
      type: 'UPDATE_IMAGE_SHAPE',
      value: {
        id: image.id,
        shape: newValue as EmailSignatureShapes,
      },
    });

    let newCrop: EmailSignatureCrop;
    if (newValue === EmailSignatureShapes.CIRCLE) {
      if (!image.crop) {
        return;
      }
      const copyOfPreviousCrop = cloneDeep(image.crop);
      const smallestDimension = Math.min(copyOfPreviousCrop.height, copyOfPreviousCrop.width);
      const dx =
        copyOfPreviousCrop.width > copyOfPreviousCrop.height
          ? (copyOfPreviousCrop.width - copyOfPreviousCrop.height) / 2
          : 0;
      const dy =
        copyOfPreviousCrop.height > copyOfPreviousCrop.width
          ? (copyOfPreviousCrop.height - copyOfPreviousCrop.width) / 2
          : 0;
      newCrop = {
        ...copyOfPreviousCrop,
        x: copyOfPreviousCrop.x + dx,
        y: copyOfPreviousCrop.y + dy,
        width: smallestDimension,
        height: smallestDimension,
      };
    } else {
      if (!image.crop) {
        return;
      }
      const copyOfPreviousCrop = cloneDeep(image.crop);
      newCrop = { ...copyOfPreviousCrop };
    }

    if (displayImagesInViewMode[image.id]) {
      emailSignatureDispatch({ type: 'UPDATE_IMAGE_CROP', value: { id: image.id, crop: newCrop } });
      handleChangeImageViewMode(false, image.id);
      return;
    } else {
      handleUpdateImageShape(image.id, newValue as EmailSignatureShapes, newCrop);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    if (!imageSectionDiv) return;
    registerNode(`IMAGE-${image.id}`, {
      panelBelongTo: panelIndex,
      node: imageSectionDiv,
    });
  }, [imageSectionDiv, panelIndex, registerNode, scrollIntoView, image.id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    const handleScrollToImage = (imageId: string) => {
      if (isInTransitionState || imageId !== image.id) {
        return;
      }
      scrollIntoView(`IMAGE-${image.id}`, SideEditorScrollType.START);
      setOpenEditingBody(true);
    };
    Emitter.on('SCROLL_TO_IMAGE', handleScrollToImage);

    return () => {
      Emitter.off('SCROLL_TO_IMAGE', handleScrollToImage);
    };
  }, [changeTab, image.id, isInTransitionState, scrollIntoView]);

  return (
    <>
      <EmailSignatureImageBox ref={imageSectionDivRef}>
        <EmailSignatureImagesBoxHeader onClick={() => setOpenEditingBody(!openEditingBody)}>
          <EmailSignatureImagesBoxHeaderContents>
            <span className={styles['image-title-container']}>
              <span className={styles['image-title']}>{getImageTitle(image.type)}</span>
              {image.isPrimary && neighboringImageIndex !== -1 && (
                <span className={styles['primary-badge']}>Primary</span>
              )}
            </span>

            {!isSignatureLocked && (
              <span className={styles['images-box-icons']}>
                <EmailSignatureSpanButton onActivate={() => setOpenEditingBody(!openEditingBody)}>
                  <FontAwesomeIcon icon={faPencil} size={'lg'} />
                </EmailSignatureSpanButton>
                <EmailSignatureSpanButton onActivate={handleDeleteImage}>
                  <FontAwesomeIcon icon={faTrash} size={'lg'} />
                </EmailSignatureSpanButton>
                {neighboringImageIndex !== -1 && (
                  <span ref={makePrimaryButtonElRef}>
                    <EmailSignatureSpanButton onActivate={() => setShowMakePrimaryPopover(true)}>
                      <FontAwesomeIcon icon={faEllipsisV} size={'lg'} />
                    </EmailSignatureSpanButton>
                  </span>
                )}
                {neighboringImageIndex !== -1 && (
                  <Popover
                    positioner={positionBounded({
                      offsetX: 8,
                      xPreference: 'right',
                      alignY: 'cover',
                      yPreference: 'bottom',
                    })}
                    targetNode={makePrimaryButtonEl}
                    show={showMakePrimaryPopover}
                    onClose={() => setShowMakePrimaryPopover(false)}
                    preventOutsideInteraction
                  >
                    <button
                      className={styles['make-primary-button']}
                      onClick={(event: MouseEvent) => {
                        event.stopPropagation();
                        if (image.isPrimary) {
                          emailSignatureDispatch({
                            type: 'MAKE_IMAGE_SECONDARY',
                            value: image.id,
                          });
                        } else {
                          emailSignatureDispatch({
                            type: 'MAKE_IMAGE_PRIMARY',
                            value: image.id,
                          });
                        }
                        setShowMakePrimaryPopover(false);
                      }}
                    >
                      <span className={styles['make-primary-button-title']}>
                        {image.isPrimary ? 'Make secondary' : 'Make primary'}
                      </span>
                      <span className={styles['make-primary-button-message']}>
                        {getMakePrimaryOrSecondaryImageMessageForCompactLayout(
                          emailSignature.images[neighboringImageIndex],
                          image,
                        )}
                      </span>
                    </button>
                  </Popover>
                )}
              </span>
            )}
          </EmailSignatureImagesBoxHeaderContents>
        </EmailSignatureImagesBoxHeader>
        {openEditingBody && <EmailSignatureImagesBoxHeaderDivider />}
        <EmailSignatureImagesBoxBody collapsed={!openEditingBody}>
          {!originalImagesUrls[image.id] && !displayImagesInViewMode[image.id] && (
            <div className={styles['images-field']}>
              <ImageSelector
                onSelect={(imageReference) => {
                  handleUploadOriginalImageUrl(imageReference, image.id);
                  emailSignatureDispatch({
                    type: 'UPDATE_IMAGE_URL',
                    value: { id: image.id, imageUrl: imageReference, crop: null },
                  });
                }}
                maxDimension={1500}
                disabled={isSignatureLocked}
              />
            </div>
          )}

          {originalImagesUrls[image.id] && !displayImagesInViewMode[image.id] && (
            <div className={styles['image-editor-framer-container']}>
              {show && (
                <ImageEditorFrame>
                  <ImageEditor
                    key={toString(image.shape === EmailSignatureShapes.CIRCLE)}
                    cropShape={image.shape === EmailSignatureShapes.CIRCLE ? 'circle' : 'rectangle'}
                    aspectRatio={image.shape === EmailSignatureShapes.CIRCLE ? 1 : undefined}
                    image={originalImagesUrls[image.id]}
                    onEdit={handleImageEditing}
                    crop={image.crop || undefined}
                  />
                  <ImageEditorButtons>
                    {previouslyCroppedImageUrl && (
                      <ImageEditorButton onClick={handleCancelCroppedImageChange}>
                        Cancel
                      </ImageEditorButton>
                    )}
                    {!previouslyCroppedImageUrl && (
                      <ImageEditorButton onClick={handleChangeOriginalImage}>
                        Change
                      </ImageEditorButton>
                    )}
                    <ImageEditorButton onClick={handleConfirmCroppedImage}>
                      Confirm
                    </ImageEditorButton>
                  </ImageEditorButtons>
                </ImageEditorFrame>
              )}
            </div>
          )}

          {displayImagesInViewMode[image.id] && image.imageUrl && (
            <div className={styles['images-field']}>
              <EmailSignatureEditorImageViewMode
                isLocked={isSignatureLocked}
                onEditImage={handleStartEditingImage}
                onChangeImage={handleChangeOriginalImage}
                imageSrc={image.imageUrl}
              />
            </div>
          )}

          {image.imageUrl && (
            <>
              <div className={styles['images-field']}>
                <div className={styles['images-field-label']}>Shape</div>
                <div className={styles['images-field-input']}>
                  <FormSelectorGroup
                    className={styles['form-selector-icons-group']}
                    selectedValue={image.shape}
                    onChange={handleChangeImageShape}
                    size="sm"
                  >
                    <div className={styles['icon-selector']}>
                      <FormSelector
                        disabled={isSignatureLocked}
                        value={EmailSignatureShapes.CIRCLE}
                      >
                        <FontAwesomeIcon icon={faCircle}></FontAwesomeIcon>
                      </FormSelector>
                    </div>

                    <div className={styles['icon-selector']}>
                      <FormSelector
                        disabled={isSignatureLocked}
                        value={EmailSignatureShapes.SQUARE}
                      >
                        <FontAwesomeIcon icon={faSquareFull}></FontAwesomeIcon>
                      </FormSelector>
                    </div>

                    <div className={styles['icon-selector']}>
                      <FormSelector
                        disabled={isSignatureLocked}
                        value={EmailSignatureShapes.SQUIRCLE}
                      >
                        <EmailSignatureSquircle />
                      </FormSelector>
                    </div>
                  </FormSelectorGroup>
                </div>
              </div>

              <div className={styles['images-field']}>
                <div className={styles['images-field-label']}>Size</div>
                <div className={styles['images-field-input']}>
                  <FormSelectorGroup
                    className={styles['form-selector-group']}
                    selectedValue={image.size}
                    onChange={(newValue) =>
                      emailSignatureDispatch({
                        type: 'UPDATE_IMAGE_SIZE',
                        value: {
                          id: image.id,
                          size: newValue as EmailSignatureSizes,
                        },
                      })
                    }
                    size="sm"
                  >
                    <div className={styles['form-selector']}>
                      <FormSelector disabled={isSignatureLocked} value={EmailSignatureSizes.SMALL}>
                        Small
                      </FormSelector>
                    </div>
                    <div className={styles['form-selector']}>
                      <FormSelector disabled={isSignatureLocked} value={EmailSignatureSizes.MEDIUM}>
                        Medium
                      </FormSelector>
                    </div>
                    <div className={styles['form-selector']}>
                      <FormSelector disabled={isSignatureLocked} value={EmailSignatureSizes.LARGE}>
                        Large
                      </FormSelector>
                    </div>
                  </FormSelectorGroup>
                </div>
              </div>
              {ENABLE_EXTERNAL_LINK_FOR_IMAGE && (
                <div className={styles['images-field']}>
                  <div className={styles['images-field-input']}>
                    <Input
                      disabled={isSignatureLocked}
                      value={externalLink || image.externalLink}
                      onChange={(newValue: string) => {
                        const uri = getURIFromURLFieldValue('link', newValue);

                        setExternalLink(newValue);

                        emailSignatureDispatch({
                          type: 'UPDATE_IMAGE_EXTERNAL_LINK',
                          value: { id: image.id, externalLink: uri ?? '' },
                        });
                      }}
                      label={'External link'}
                      contained
                      enableClearButton
                      hideAllErrorMessages
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {isSignatureLocked && (
            <EmailSignatureLockLayer
              message={
                'You cannot edit this image, please contact your admin if you have any questions'
              }
            />
          )}
        </EmailSignatureImagesBoxBody>
      </EmailSignatureImageBox>
    </>
  );
};

export default EmailSignatureEditorImageSection;
